import * as XLSX from 'xlsx';

/**
 * Utilidades para exportar los resultados de la Consulta de Bitácoras a un archivo Excel (.xlsx).
 *
 * El objetivo es exportar EXACTAMENTE lo que el usuario ve en la lista:
 *   - Solo las columnas visibles, en el orden en que están configuradas.
 *   - Todos los registros filtrados/ordenados (no solo la página visible).
 *   - Con el mismo formato de celda que muestra la tabla (fechas legibles, "-" para vacíos).
 *
 * La lógica de construcción de datos se mantiene en funciones puras para poder probarla
 * sin necesidad de escribir archivos en disco.
 */

// Columnas de fecha que en la tabla se muestran con `new Date(val).toLocaleString()`.
export const DATE_COLUMN_IDS = [
  'FechaUltimaModificacion',
  'OrigenFechaHoraSalida',
  'DestinoFechaHoraLlegada',
];

// Columnas de acciones (botones) que no representan datos y por tanto no se exportan.
export const ACTION_COLUMN_IDS = ['acciones1', 'acciones2'];

/**
 * Devuelve el valor de una celda tal y como se muestra en la tabla de resultados.
 * Replica la lógica de renderizado de BitacorasResultsTable para mantener consistencia
 * entre "lo que se ve" y "lo que se exporta".
 *
 * @param {Object} bitacora Registro de bitácora.
 * @param {String} columnId Identificador de la columna.
 * @param {Array<String>} [dateColumnIds] Columnas a formatear como fecha. Por
 *   defecto, las de Bitácoras (DATE_COLUMN_IDS); la vista de facturas
 *   timbradas pasa las suyas propias (FechaFactura, FechaCreacion).
 * @returns {String|Number} Valor formateado para la celda.
 */
export const formatBitacoraCell = (bitacora, columnId, dateColumnIds = DATE_COLUMN_IDS) => {
  const raw = bitacora?.[columnId];

  if (dateColumnIds.includes(columnId)) {
    return raw ? new Date(raw).toLocaleString() : '-';
  }

  // Se conserva el tipo original (los números siguen siendo numéricos en Excel).
  return raw ?? '-';
};

/**
 * Construye la matriz de datos (encabezado + filas) lista para volcarse a una hoja.
 *
 * @param {Array<Object>} data Registros a exportar (ya filtrados y ordenados).
 * @param {Array<Object>} columns Columnas visibles en su orden actual.
 * @param {Object} [options]
 * @param {Array<String>} [options.dateColumnIds] Columnas a formatear como fecha.
 * @param {Array<String>} [options.actionColumnIds] Columnas de botones, que no se exportan.
 * @returns {{ exportColumns: Array, header: Array<String>, rows: Array<Array> }}
 */
export const buildBitacorasSheetData = (data, columns, options = {}) => {
  const {
    dateColumnIds = DATE_COLUMN_IDS,
    actionColumnIds = ACTION_COLUMN_IDS,
  } = options;

  const exportColumns = (columns || []).filter(
    (col) => !actionColumnIds.includes(col.id)
  );

  const header = exportColumns.map((col) => col.label);

  const rows = (data || []).map((registro) =>
    exportColumns.map((col) => formatBitacoraCell(registro, col.id, dateColumnIds))
  );

  return { exportColumns, header, rows };
};

/**
 * Genera un nombre de archivo con marca de tiempo, ej: "Bitacoras_2026-07-09_143512.xlsx".
 * Recibe la fecha como parámetro para poder probarse de forma determinista.
 *
 * @param {Date} date Fecha base (por defecto ahora).
 * @param {String} prefix Prefijo del archivo.
 * @returns {String} Nombre del archivo.
 */
export const buildExportFileName = (date = new Date(), prefix = 'Bitacoras') => {
  const pad = (n) => String(n).padStart(2, '0');
  const stamp =
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  return `${prefix}_${stamp}.xlsx`;
};

/**
 * Exporta los registros indicados a un archivo Excel y dispara su descarga en el navegador.
 *
 * @param {Array<Object>} data Registros a exportar (ya filtrados y ordenados).
 * @param {Array<Object>} columns Columnas visibles en su orden actual.
 * @param {Object} [options]
 * @param {String} [options.fileName] Nombre del archivo a generar.
 * @param {String} [options.sheetName] Nombre de la hoja.
 * @returns {{ rowCount: Number, columnCount: Number }} Resumen de lo exportado.
 */
export const exportBitacorasToExcel = (data, columns, options = {}) => {
  const { exportColumns, header, rows } = buildBitacorasSheetData(data, columns, options);

  const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);

  // Ancho de columnas aproximado a partir del minWidth definido en la configuración.
  worksheet['!cols'] = exportColumns.map((col) => ({
    wch: Math.min(45, Math.max(12, Math.round((col.minWidth || 120) / 7))),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, options.sheetName || 'Bitacoras');
  XLSX.writeFile(workbook, options.fileName || buildExportFileName());

  return { rowCount: rows.length, columnCount: header.length };
};
